import React, { useEffect, useMemo, useState } from 'react';
import { pickBy } from 'lodash';
import Table from 'partial/components/Table';
import SearchBar from 'partial/components/input/SearchBar';
import Pagination from 'partial/components/Pagination';
import SwitchToggle from 'partial/components/SwitchToggle';
import Badges from 'modules/common/components/Badge';
import { BsTrash, BsPencil } from 'react-icons/bs';
import Button from 'partial/components/Button';
import { useModuleContext } from 'partial/wrappers/ModuleWrapper';
import { aclHelper } from 'helper';

import { useMaskNameList } from './hooks';
import { useOpenActiveMaskName } from './modals/ActiveMaskName';
import { useOpenDeleteMaskName } from './modals/DeleteMaskName';
import { useOpenSendMessage } from './modals/SendMessage';

const INITIAL_STATE = {
  keyword: '',
};

export function Subscriptions() {
  const { setSubLabel } = useModuleContext();

  const [filter, setFilter] = useState(INITIAL_STATE);
  const [page, setPage] = useState({ page: '' });
  const payload = useMemo(
    () =>
      pickBy({
        ...filter,
        ...page,
      }),
    [filter, page],
  );

  useEffect(() => {
    setPage({ page: '' });
  }, [filter]);

  const [isLoading, list, meta] = useMaskNameList(payload);
  const openActiveMaskName = useOpenActiveMaskName();
  const openDeleteMaskName = useOpenDeleteMaskName();
  const openSendMessage = useOpenSendMessage();

  const canDelete = aclHelper('sender_name', 'd');
  const canSendMessage = aclHelper('sender_name', 'w');
  const canActivate = aclHelper('sender_name', 'u');

  useEffect(() => {
    if (!meta) return;
    setSubLabel(
      meta?.total === 0
        ? ''
        : `Total of ${meta?.total} mask name subscription${
            meta?.total === 1 ? '' : 's'
          }`,
    );
    return () => setSubLabel('');
  }, [meta, setSubLabel]);

  const renderAction = (data) => (
    <div className="flex justify-end gap-2">
      {canDelete && (
        <Button
          icon={BsTrash}
          outline
          size="xs"
          onClick={() => openDeleteMaskName(data)}
          className="font-bold tracking-widest"
        >
          Delete
        </Button>
      )}
      {canSendMessage && (
        <Button
          icon={BsPencil}
          outline
          size="xs"
          onClick={() => openSendMessage(data)}
          className="font-bold tracking-widest"
        >
          Send Message
        </Button>
      )}
      {canActivate && (
        <button type="button" onClick={() => openActiveMaskName(data)}>
          <SwitchToggle value={!data?.is_deactivate} />
        </button>
      )}
    </div>
  );

  const format = [
    { label: 'MASK NAME', key: 'name' },
    { label: 'ORGANIZATION/USER', key: 'organization_name' },
    { label: 'BILLING', key: 'billing_ref' },
    { label: 'START DATE', key: 'subscribed_at' },
    { label: 'END DATE', key: 'expired_at' },
    {
      id: 'status',
      key: ({ status }) => (
        <Badges status={status} containerClassName="!px-1" />
      ),
      label: 'status',
      labelClassName: 'whitespace-nowrap flex justify-center',
    },
    ...(canDelete || canSendMessage || canActivate
      ? [
          {
            id: 'action',
            key: (data) => <div>{renderAction(data)}</div>,
            label: 'ACTION',
            labelClassName: 'whitespace-nowrap flex justify-center',
            contentClassName: 'px-6 whitespace-nowrap flex justify-center',
          },
        ]
      : []),
  ];

  return (
    <section className="p-6 bg-white drop-shadow rounded-lg h-full flex flex-col">
      <div className="mb-5 flex items-center space-x-5">
        <SearchBar
          name="keyword"
          value={filter.keyword}
          onChange={setFilter}
          inputClassName="py-[1.05rem] "
        />
      </div>
      <div className="flex flex-col gap-10 min-h-[65vh]">
        <Table isLoading={isLoading} data={list} format={format} />
        <Pagination value={meta} onChange={setPage} />
      </div>
    </section>
  );
}

export default Subscriptions;
